import {
  DATE_ISO_FORMAT_DATE_FNS,
  DayOfWeek,
  NATURAL_NUMBER_PATTERN,
  SchedulingType,
  TIME_SHORT_FORMAT,
  UliActivityType,
} from '../../../../../common/constants';
import { Validators } from '@angular/forms';
import { mustNotBeBlankValidator } from '../../../../../common/utils';
import { MatDialogRef } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { CompartmentRuleListItem } from './compartment-rule-list.model';
import { futureDateValidator, schedulingTypeValidator, timeRangeValidator } from './compartment-rule-item.validators';
import { ConfirmationDialogAfterClosedModel } from '../../../../../ui-kit';
import { LocalDate } from '../../../../models/local-date.model';
import { carrierSapValidator } from './compartment-carrier.validator';
import { CompartmentRuleTimeFrameEnum } from './compartment-rule-item.validators';
import { CompartmentSapModel } from './compartment.model';
import { format } from 'date-fns';
import { AddressModel, CarrierModel, ControlProperty } from '../../../../../common/models';
import moment, { Moment } from 'moment';

export interface CompartmentRuleItemBaseParams {
  hubId: number;
  compartmentId: number;
}

export interface CompartmentRuleItemURIParams extends CompartmentRuleItemBaseParams {
  ruleId: number;
}

export interface CompartmentRuleDeleteItemParams extends CompartmentRuleItemBaseParams {
  ruleIds: number[];
  dialog: MatDialogRef<ConfirmationDialogAfterClosedModel>;
}

export interface CompartmentRuleDeleteItemsParams extends CompartmentRuleDeleteItemParams {
  selection: SelectionModel<CompartmentRuleListItem>;
}

export interface CompartmentRuleItemCreateParams extends CompartmentRuleItemBaseParams {
  body: Partial<CompartmentRuleItemOutput>;
  dialog?: MatDialogRef<ConfirmationDialogAfterClosedModel>;
}

export interface CompartmentRuleItemParams extends CompartmentRuleItemURIParams {
  body: Partial<CompartmentRuleItem>;
}

export interface CompartmentRuleItemGetDocksParams extends CompartmentRuleItemBaseParams {
  filter: string;
  page: number;
  size: number;
}

export interface CompartmentRuleItemGetCarriersParams {
  filter: string;
  limit: number;
}

export interface OverlappingRuleErrorModel {
  error: {
    dockId: number;
    dockName: string;
    message: string;
    slots: [
      {
        slotId: number;
        timeFrame: {
          end: LocalDate;
          start: LocalDate;
        };
      },
    ];
  };
}

export class CompartmentRuleItemBase {
  id: number;
  name: string;
  activityTypes: UliActivityType[] = [];
  pickupAddresses: AddressModel[] = [];
  deliveryAddresses: AddressModel[] = [];
  dockId: number;
  dockName: string;
  erp = false;
  compartmentSapClientViews: CompartmentSapModel[] = [];
  carrier: CarrierModel;
  subCarriers: CarrierModel[] = [];
  truckPlates: string[] = [];
  schedulingType: SchedulingType = SchedulingType.Weekly;
  interval = 1;
  day: DayOfWeek[] = [];

  constructor(params?: CompartmentRuleItem) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class CompartmentRuleItem extends CompartmentRuleItemBase {
  startTime: Moment = null;
  endTime: Moment = null;
  validFrom: Date = null;
  validTo: Date = null;

  constructor(params?: CompartmentRuleItem) {
    super();
    if (params) {
      Object.assign(this, params);

      this.startTime = moment(params.startTime, [TIME_SHORT_FORMAT]);
      this.endTime = moment(params.endTime, [TIME_SHORT_FORMAT]);
      this.validFrom = new Date(params.validFrom);
      this.validTo = new Date(params.validTo);
    }
  }
}

export class CompartmentRuleItemOutput extends CompartmentRuleItemBase {
  startTime: string;
  endTime: string;
  validFrom: string;
  validTo: string;

  constructor(params?: CompartmentRuleItem) {
    super();
    if (params) {
      Object.assign(this, params);

      this.startTime = moment(params.startTime).format(TIME_SHORT_FORMAT);
      this.endTime = moment(params.endTime).format(TIME_SHORT_FORMAT);
      this.validFrom = format(new Date(params.validFrom), DATE_ISO_FORMAT_DATE_FNS);
      this.validTo = format(new Date(params.validTo), DATE_ISO_FORMAT_DATE_FNS);
    }
  }
}

export class CompartmentRuleItemForm {
  constructor(
    model: CompartmentRuleItem,
    public id: ControlProperty<number> = model.id,
    public name: ControlProperty<string> = [model.name, [Validators.required, mustNotBeBlankValidator]],
    public activityTypes: ControlProperty<UliActivityType[]> = [model.activityTypes, [Validators.required]],
    public dockId: ControlProperty<number> = [model.dockId, [Validators.required]],
    public dockName: ControlProperty<string> = model.dockName,
    public truckPlates: ControlProperty<string[]> = model.truckPlates,
    public erp: ControlProperty<boolean> = [{ value: model.erp, disabled: true }, []],
    public interval: ControlProperty<number> = [
      model.interval,
      [Validators.required, Validators.pattern(NATURAL_NUMBER_PATTERN)],
    ],
    public day: ControlProperty<string[]> = [model.day, [Validators.required]],
    public carrier: ControlProperty<CarrierModel> = [model.carrier, [Validators.required, carrierSapValidator]],
    public subCarriers: ControlProperty<CarrierModel[]> = [model.subCarriers, [carrierSapValidator]],
    public deliveryAddresses: ControlProperty<AddressModel[]> = [model.deliveryAddresses, []],
    public pickupAddresses: ControlProperty<AddressModel[]> = [model.pickupAddresses, []],
    public startTime: ControlProperty<Moment> = [
      model.startTime ? moment(model.startTime, [TIME_SHORT_FORMAT]) : model.startTime,
      [Validators.required, timeRangeValidator(CompartmentRuleTimeFrameEnum.StartTime)],
    ],
    public endTime: ControlProperty<Moment> = [
      { value: model.endTime ? moment(model.endTime, [TIME_SHORT_FORMAT]) : model.endTime, disabled: true },
      [Validators.required, timeRangeValidator(CompartmentRuleTimeFrameEnum.EndTime)],
    ],
    public validFrom: ControlProperty<Date> = [model.validFrom, [Validators.required, futureDateValidator]],
    public validTo: ControlProperty<Date> = [model.validTo, [Validators.required]],
    public schedulingType: ControlProperty<SchedulingType> = [
      model.schedulingType,
      [Validators.required, schedulingTypeValidator],
    ],
    public compartmentSapClientViews: ControlProperty<CompartmentSapModel[]> = [
      { value: model.compartmentSapClientViews, disabled: true },
      [],
    ],
  ) {}
}
